<template lang="pug">
div(:id="embedded ? undefined : 'aiChatPage'" :class="{ 'ai-chat-embed-root': embedded }")
	Navbar(v-if="!embedded" tab="aichat" :siteId="navbarSiteId" :site="site")
	.ai-chat-auth-error(v-if="(embedded || isAdmin) && atriumError")
		p.ai-chat-auth-error-title Could not connect to Atrium
		p.ai-chat-auth-error-detail {{ atriumError }}
	main.ai-chat(:class="{ 'ai-chat--embedded': embedded }" v-else-if="embedded || isAdmin")
		AiChatSidebar(
			:conversations="conversations"
			:selected-id="conversationId"
			@new-chat="startNewChat"
			@select="selectConversation"
		)
		section.transcript-pane
			.transcript(ref="transcript" @scroll.passive="onTranscriptScroll")
				.empty(v-if="showEmpty")
					v-icon.empty-icon mdi-creation
					h1 Ask anything
					p Help with dashboards, sites, devices, and air quality.
					.prompts
						button.prompt(v-for="p in starterPrompts" :key="p" type="button" :disabled="busy" @click="send(p)") {{ p }}
				.skeleton(v-else-if="showSkeleton")
					.msg.is-user
						.skel.skel-user
					.msg.is-assistant
						.skel.skel-line
						.skel.skel-line.is-mid
						.skel.skel-line.is-short
					.msg.is-user
						.skel.skel-user.is-alt
					.msg.is-assistant
						.skel.skel-line
						.skel.skel-line.is-short
				.msg(
					v-for="m in displayMessages"
					:key="m.rowKey || m.objectId"
					:class="m.senderType === 'USER' ? 'is-user' : 'is-assistant'"
				)
					template(v-if="m.senderType === 'SYSTEM'")
						AiChatThinking(
							v-if="m.thinkingLive || m.thinking || m.thoughtDurationSec"
							:text="m.thinking || ''"
							:html="markdownHtml(m.thinking || '')"
							:streaming="!!m.thinkingLive && !m.content"
							:duration-sec="m.thoughtDurationSec"
						)
						.bubble.markdown(v-if="m.content" v-html="markdownHtml(m.content)")
					.bubble(v-else) {{ m.content }}
					.msg-actions(v-if="m.senderType === 'SYSTEM' && m.content")
						v-tooltip(top)
							template(v-slot:activator="{ on, attrs }")
								button.msg-action(
									type="button"
									:class="{ 'is-copying': isCopied(m) }"
									v-bind="attrs"
									v-on="on"
									@click="copyMessage(m)"
								)
									v-icon.msg-action-icon(:class="{ 'is-copied': isCopied(m) }" :key="isCopied(m) ? copiedAt : 'copy'") {{ isCopied(m) ? 'mdi-check' : 'mdi-content-copy' }}
							span {{ isCopied(m) ? 'Copied' : 'Copy' }}
						v-tooltip(top v-if="canRegenerate(m)")
							template(v-slot:activator="{ on, attrs }")
								button.msg-action.is-regen(
									type="button"
									:disabled="busy"
									v-bind="attrs"
									v-on="on"
									@click="regenerate(m)"
								)
									v-icon.msg-action-icon.is-regen mdi-refresh
							span Regenerate
				.msg.is-assistant.stream-failed(v-if="streamFailed")
					p.stream-failed-copy Response failed. Retry?
					button.haq-btn.haq-btn-primary.stream-failed-retry(
						type="button"
						:disabled="busy"
						@click="retryFailedStream"
					) Retry
			.composer
				.composer-box
					textarea(
						ref="composer"
						v-model="draft"
						placeholder="Ask anything"
						rows="4"
						:disabled="hydrating"
						@input="resizeComposer"
						@keydown.enter.exact.prevent="onEnter"
					)
					button.send-icon(
						type="button"
						:disabled="!canSend && !streaming"
						:title="streaming ? 'Stop' : 'Send'"
						@click="streaming ? stop() : send()"
					)
						v-icon {{ streaming ? 'mdi-stop' : 'mdi-arrow-up' }}
</template>
<script>
import Navbar from '@/components/Navbar.vue';
import AiChatSidebar from '@/components/aiChat/AiChatSidebar.vue';
import AiChatThinking from '@/components/aiChat/AiChatThinking.vue';
import { getAtriumClient, resolveGeminiModel, mapConversation, mapMessage, clearAtriumClientCache, atriumAuthErrorMessage } from '../../assets/js/atriumClient';
import { renderMarkdown } from '../../utils/renderMarkdown';
import { firstAccessibleSite } from '../../assets/js/siteAccess';
import './ai-chat.sass';

const STARTER_PROMPTS = [
	'Summarize what HawkenAQ dashboards are for',
	'How should I share a dashboard with one site?',
	'What does a high CO2 reading usually mean?'
];

/**
 * Admin-only AI chat workspace: conversation list, transcript, and composer.
 * Full page wraps this in `#aiChatPage`; the right dock embeds the same UI.
 */
export default {
	name: 'AiChatWorkspace',
	components: { Navbar, AiChatSidebar, AiChatThinking },
	props: {
		/**
		 * True when shown in the right-edge dock (no page chrome, no URL sync).
		 */
		embedded: { type: Boolean, default: false }
	},
	data() {
		return {
			site: null,
			panelConversationId: null,
			conversations: [],
			messages: [],
			draft: '',
			streamingText: '',
			streamingThinking: '',
			streaming: false,
			hydrating: false,
			saving: false,
			skipHydrate: false,
			streamController: null,
			pendingAssistantKey: null,
			hydrateSeq: 0,
			starterPrompts: STARTER_PROMPTS,
			copiedKey: null,
			copiedTimer: null,
			copiedAt: 0,
			hasMoreOlder: false,
			loadingOlder: false,
			thinkingStartedAt: null,
			atrium: null,
			chatModel: 'google/gemini-3.6-flash',
			atriumError: null,
			streamFailed: false,
			failedRetry: null
		};
	},
	computed: {
		/**
		 * Current project id.
		 * @returns {string|null}
		 */
		projectId() {
			return this.$route.params.projectId || (this.$store.state.project && this.$store.state.project.objectId) || null;
		},
		/**
		 * Selected conversation: URL on the full page, local id in the dock.
		 * @returns {string|null}
		 */
		conversationId() {
			if (this.embedded) return this.panelConversationId;
			return this.$route.query.conversation || null;
		},
		/**
		 * True when the current user is an admin or superAdmin.
		 * @returns {boolean}
		 */
		isAdmin() {
			const role = this.resolveUser() && this.resolveUser().role;
			return role === 'admin' || role === 'superAdmin';
		},
		/**
		 * Navbar site context.
		 * @returns {string|null}
		 */
		navbarSiteId() {
			return this.$route.query.siteId || this.site && this.site.objectId || null;
		},
		/**
		 * True while a send or stream is in flight.
		 * @returns {boolean}
		 */
		busy() {
			return this.streaming || this.saving || this.hydrating;
		},
		/**
		 * Send is allowed when there is text and nothing is streaming.
		 * @returns {boolean}
		 */
		canSend() {
			return !this.atriumError && !this.busy && !!(this.draft && this.draft.trim());
		},
		/**
		 * Empty landing state (no thread, no messages).
		 * @returns {boolean}
		 */
		showEmpty() {
			return !this.atriumError && !this.messages.length && !this.streaming && !this.streamingText && !this.streamingThinking && !this.hydrating && !this.streamFailed;
		},
		/**
		 * True while switching conversations and the transcript is empty.
		 * @returns {boolean}
		 */
		showSkeleton() {
			return this.hydrating && !this.messages.length && !this.streaming;
		},
		/**
		 * Stored messages plus the in-flight assistant bubble or loading dots.
		 * @returns {Array}
		 */
		displayMessages() {
			return this.messages;
		}
	},
	watch: {
		conversationId(id) {
			if (this.skipHydrate) {
				this.skipHydrate = false;
				return;
			}
			if (id) this.loadMessages(id);
			else {
				this.messages = [];
				this.hydrating = false;
				this.hasMoreOlder = false;
				this.loadingOlder = false;
				this.streamFailed = false;
				this.failedRetry = null;
			}
		},
		'$store.state.user'(user) {
			if (!user) return;
			if (!this.guardAdmin()) return;
			this.bootstrap();
		},
		projectId(id) {
			if (!id) return;
			if (!this.guardAdmin()) return;
			this.bootstrap();
		}
	},
	mounted() {
		if (!this.guardAdmin()) return;
		this.bootstrap();
		this.$nextTick(this.resizeComposer);
	},
	beforeDestroy() {
		this.clearCopiedTimer();
		this.stop();
	},
	methods: {
		/**
		 * User row from Vuex (role may sit on attributes).
		 * @returns {object|null}
		 */
		resolveUser() {
			let user = this.$store.state.user;
			if (!user) {
				try {
					const raw = localStorage.getItem('user');
					if (raw) {
						user = JSON.parse(raw);
						this.$store.commit('setUser', user);
					}
				} catch (error) {
					user = null;
				}
			}
			if (!user) return null;
			return {
				role: user.role || (user.attributes && user.attributes.role)
			};
		},
		/**
		 * Kick known non-admins home on the full page. The dock never redirects.
		 * @returns {boolean}
		 */
		guardAdmin() {
			const user = this.resolveUser();
			if (!user || !user.role) return false;
			if (user.role !== 'admin' && user.role !== 'superAdmin') {
				if (!this.embedded) this.$router.replace('/');
				return false;
			}
			const project = this.$store.state.project;
			if (project && this.$store.getters.hasAtriumOrg === false) {
				if (!this.embedded) this.$router.replace('/');
				return false;
			}
			return true;
		},
		/**
		 * Load site context, Atrium SDK client, conversation list, and the selected thread.
		 */
		async bootstrap() {
			if (!this.embedded) this.loadSiteContext();
			this.atriumError = null;
			this.atrium = null;
			if (!this.$store.getters.hasAtriumOrg || !this.projectId) return;
			try {
				this.atrium = await getAtriumClient(this.projectId);
				await this.atrium.ensureToken();
				this.chatModel = await resolveGeminiModel(this.atrium);
			} catch (error) {
				console.error(error);
				clearAtriumClientCache();
				this.atrium = null;
				this.atriumError = atriumAuthErrorMessage(error);
				return;
			}
			try {
				await this.loadConversations();
				if (this.conversationId) await this.loadMessages(this.conversationId);
			} catch (error) {
				console.error(error);
				clearAtriumClientCache();
				this.atrium = null;
				this.atriumError = atriumAuthErrorMessage(error);
			}
		},
		/**
		 * Load a site into navbar context (project-level page).
		 */
		async loadSiteContext() {
			try {
				let sites = this.$store.state.sites;
				if (!sites || !sites.length) sites = await this.$store.dispatch('getSites');
				const first = this.$store.getters.firstAccessibleSite || firstAccessibleSite(sites, this.$store.state.user, this.projectId);
				this.site = first || null;
			} catch (error) {
				console.error(error);
			}
		},
		/**
		 * Fetch this user's Atrium conversations.
		 */
		async loadConversations() {
			if (!this.atrium) throw new Error('Atrium is not connected');
			const result = await this.atrium.conversations.list();
			const rows = (result && result.conversations) || [];
			this.conversations = rows.map(mapConversation).filter(Boolean);
		},
		/**
		 * Fetch the latest page of messages for a conversation.
		 * @param {string} id
		 */
		async loadMessages(id) {
			if (!id) {
				this.messages = [];
				this.hydrating = false;
				this.hasMoreOlder = false;
				this.loadingOlder = false;
				return;
			}
			this.hydrateSeq += 1;
			const seq = this.hydrateSeq;
			this.hydrating = true;
			this.loadingOlder = false;
			this.hasMoreOlder = false;
			this.streamFailed = false;
			this.failedRetry = null;
			this.messages = [];
			if (!this.atrium) {
				this.hydrating = false;
				return;
			}
			try {
				const result = await this.atrium.conversations.messages.list(id);
				if (seq !== this.hydrateSeq) return;
				const rows = (result && result.messages) || [];
				this.messages = rows.map(mapMessage).filter(Boolean);
				this.hasMoreOlder = false;
				this.$nextTick(this.afterLatestPage);
			} catch (error) {
				if (seq !== this.hydrateSeq) return;
				console.error(error);
				window.M && window.M.toast && window.M.toast({ html: 'Failed to load chat' });
			} finally {
				if (seq === this.hydrateSeq) this.hydrating = false;
			}
		},
		/**
		 * Load the next older batch when the transcript is scrolled to the top.
		 */
		onTranscriptScroll() {
			this.maybeLoadOlder();
		},
		/**
		 * After the latest page paints, pin the transcript to the newest messages.
		 */
		afterLatestPage() {
			this.scrollToBottom();
			this.$nextTick(this.scrollToBottom);
		},
		/**
		 * Fetch older messages if the user is at the top and more rows exist.
		 */
		maybeLoadOlder() {
			if (this.hydrating || this.loadingOlder || !this.hasMoreOlder || this.showEmpty) return;
			const el = this.$refs.transcript;
			if (!el) return;
			if (el.scrollTop > 80) return;
			this.loadOlder();
		},
		/**
		 * Atrium lists the full thread; older paging is unused.
		 */
		async loadOlder() {
			this.loadingOlder = false;
			this.hasMoreOlder = false;
		},
		/**
		 * Clear the selected thread.
		 */
		startNewChat() {
			this.stop();
			this.hydrateSeq += 1;
			this.hydrating = false;
			this.messages = [];
			this.streamingText = '';
			this.streamingThinking = '';
			this.pendingAssistantKey = null;
			this.hasMoreOlder = false;
			this.loadingOlder = false;
			this.streamFailed = false;
			this.failedRetry = null;
			this.draft = '';
			this.replaceQuery(null);
		},
		/**
		 * Open a conversation from the sidebar.
		 * @param {string} id
		 */
		selectConversation(id) {
			if (!id || id === this.conversationId) return;
			if (this.streaming) this.stop();
			this.hydrateSeq += 1;
			this.messages = [];
			this.streamingText = '';
			this.streamingThinking = '';
			this.pendingAssistantKey = null;
			this.hasMoreOlder = false;
			this.loadingOlder = false;
			this.streamFailed = false;
			this.failedRetry = null;
			this.hydrating = true;
			this.replaceQuery(id);
		},
		/**
		 * Set or clear the selected conversation. Full page uses `?conversation=`.
		 * @param {string|null} id
		 */
		replaceQuery(id) {
			if (this.embedded) {
				this.panelConversationId = id || null;
				return;
			}
			const next = id || null;
			const current = this.$route.query.conversation || null;
			if (current === next) return;
			const query = Object.assign({}, this.$route.query);
			if (next) query.conversation = next;
			else delete query.conversation;
			this.$router.replace({ path: this.$route.path, query: query }).catch(function(err) {
				if (err && err.name === 'NavigationDuplicated') return;
				throw err;
			});
		},
		/**
		 * Enter sends unless Shift is held (newline).
		 */
		onEnter() {
			if (this.streaming) return;
			this.send();
		},
		/**
		 * Gateway history from stored messages (last 10).
		 * @returns {Array}
		 */
		historyPayload() {
			const ready = [];
			for (let i = 0; i < this.messages.length; i++) {
				const row = this.messages[i];
				if (row.thinkingLive && !row.content) continue;
				ready.push(row);
			}
			const list = ready.slice(-10);
			const out = [];
			for (let i = 0; i < list.length; i++) {
				const row = list[i];
				out.push({
					role: row.senderType === 'USER' ? 'user' : 'assistant',
					content: row.content || ''
				});
			}
			return out;
		},
		/**
		 * Live assistant row for the in-flight stream, if any.
		 * @returns {object|null}
		 */
		pendingAssistantRow() {
			const key = this.pendingAssistantKey;
			if (!key) return null;
			for (let i = this.messages.length - 1; i >= 0; i--) {
				if ((this.messages[i].rowKey || this.messages[i].objectId) === key) return this.messages[i];
			}
			return null;
		},
		/**
		 * Insert the in-flight assistant row so thinking keeps the same Vue node.
		 * @param {string} key
		 * @param {string} [objectId]
		 */
		beginAssistantTurn(key, objectId) {
			this.pendingAssistantKey = key;
			this.thinkingStartedAt = Date.now();
			this.streaming = true;
			this.streamingText = '';
			this.streamingThinking = '';
			this.messages.push({
				objectId: objectId || key,
				rowKey: key,
				senderType: 'SYSTEM',
				content: '',
				thinking: '',
				thinkingLive: true,
				thoughtDurationSec: null,
				model: this.chatModel
			});
		},
		/**
		 * Stamp thought duration once, when the answer starts or the stream ends.
		 */
		stampThoughtDuration() {
			const row = this.pendingAssistantRow();
			if (!row || row.thoughtDurationSec != null) return;
			if (this.thinkingStartedAt == null) {
				row.thoughtDurationSec = 1;
				return;
			}
			row.thoughtDurationSec = Math.max(1, Math.ceil((Date.now() - this.thinkingStartedAt) / 1000));
		},
		/**
		 * Write stream tokens onto the in-flight assistant row.
		 * @param {{ content?: string, thinking?: string }} fields
		 */
		patchPendingAssistant(fields) {
			const row = this.pendingAssistantRow();
			if (!row || !fields) return;
			if (fields.content != null) {
				row.content = fields.content;
				this.stampThoughtDuration();
			}
			if (fields.thinking != null) row.thinking = fields.thinking;
		},
		/**
		 * Keep the in-flight row as the saved assistant turn.
		 * @param {string} content
		 * @param {string} thinking
		 */
		finishPendingAssistant(content, thinking) {
			this.stampThoughtDuration();
			const row = this.pendingAssistantRow();
			if (!row) return;
			row.content = content;
			row.thinking = thinking || '';
			row.thinkingLive = false;
		},
		/**
		 * Drop the in-flight assistant row (stop, empty stream, or failed stream).
		 */
		dropPendingAssistant() {
			const key = this.pendingAssistantKey;
			if (!key) return;
			this.messages = this.messages.filter(function(row) {
				return (row.rowKey || row.objectId) !== key;
			});
			this.pendingAssistantKey = null;
			this.thinkingStartedAt = null;
		},
		/**
		 * Persist the user turn so it is kept even if the assistant stream fails.
		 * @param {string} conversationId
		 * @param {string} userKey
		 * @param {string} text
		 * @returns {Promise<boolean>}
		 */
		async persistUserMessage(conversationId, userKey, text) {
			if (!this.atrium || !text) return false;
			if (this.failedRetry && this.failedRetry.userPersisted) return true;
			try {
				const saved = await this.atrium.conversations.messages.create(conversationId, {
					userContent: text
				});
				this.applySavedIds(userKey, '', (saved && saved.messages) || []);
				if (this.failedRetry) this.failedRetry.userPersisted = true;
				await this.loadConversations();
				return true;
			} catch (error) {
				console.error(error);
				return false;
			}
		},
		/**
		 * Keep the user turn and show the in-transcript retry notice.
		 */
		markStreamFailed() {
			this.streamingText = '';
			this.streamingThinking = '';
			this.dropPendingAssistant();
			this.streamFailed = true;
		},
		/**
		 * Replay the last user turn after a failed assistant stream.
		 */
		async retryFailedStream() {
			if (!this.streamFailed || this.busy || !this.atrium || !this.failedRetry) return;
			const spec = this.failedRetry;
			this.streamFailed = false;
			this.beginAssistantTurn(spec.assistantKey, spec.assistantObjectId || spec.assistantKey);
			this.$nextTick(this.scrollToBottom);
			try {
				let conversationId = this.conversationId;
				if (!conversationId) {
					const created = await this.atrium.conversations.create({ model: this.chatModel });
					const row = mapConversation((created && created.conversation) || created) || {};
					conversationId = row.objectId;
					this.skipHydrate = true;
					this.replaceQuery(conversationId);
					this.conversations = [row].concat(this.conversations);
				}
				if (!this.streaming) return;
				if (spec.mode !== 'regenerate' && spec.userText && !spec.userPersisted) {
					await this.persistUserMessage(conversationId, spec.userKey, spec.userText);
				}
				if (!this.streaming) return;
				await this.streamAssistant(this.historyPayload(), conversationId);
				const assistantText = this.streamingText;
				const thinkingText = this.streamingThinking;
				if (!assistantText) {
					this.markStreamFailed();
					return;
				}
				this.finishPendingAssistant(assistantText, thinkingText);
				this.streamingText = '';
				this.streamingThinking = '';
				this.saving = true;
				if (spec.mode === 'regenerate' && spec.assistantObjectId) {
					const saved = await this.atrium.requestJson(
						'PATCH',
						'/api/atrium/conversations/' + conversationId + '/messages/' + spec.assistantObjectId,
						{ json: { content: assistantText, model: this.chatModel } }
					);
					const row = (saved && saved.message) || {};
					const last = this.messages[this.messages.length - 1];
					if (last && (last.rowKey || last.objectId) === spec.assistantKey) {
						last.objectId = row.id || row.objectId || last.objectId;
						last.content = assistantText;
						last.thinking = thinkingText;
					}
				} else if (spec.userPersisted) {
					const saved = await this.atrium.conversations.messages.create(conversationId, {
						assistantContent: assistantText,
						model: this.chatModel
					});
					this.applySavedIds(spec.userKey, spec.assistantKey, (saved && saved.messages) || []);
				} else {
					const saved = await this.atrium.conversations.messages.create(conversationId, {
						userContent: spec.userText,
						assistantContent: assistantText,
						model: this.chatModel
					});
					this.applySavedIds(spec.userKey, spec.assistantKey, (saved && saved.messages) || []);
				}
				await this.loadConversations();
			} catch (error) {
				if (error && error.name === 'AbortError') return;
				console.error(error);
				this.markStreamFailed();
			} finally {
				this.saving = false;
				this.streaming = false;
				this.streamController = null;
				this.pendingAssistantKey = null;
			}
		},
		/**
		 * Create a thread if needed, stream, then persist.
		 * @param {string} [preset]
		 */
		async send(preset) {
			const text = String(preset != null ? preset : this.draft || '').trim();
			if (!text || this.busy || !this.atrium) return;
			this.draft = '';
			this.$nextTick(this.resizeComposer);
			const userKey = 'local-user-' + Date.now();
			const assistantKey = 'local-assistant-' + Date.now();
			this.messages.push({
				objectId: userKey,
				rowKey: userKey,
				senderType: 'USER',
				content: text
			});
			this.beginAssistantTurn(assistantKey);
			this.failedRetry = {
				mode: 'send',
				userText: text,
				userKey: userKey,
				assistantKey: assistantKey,
				assistantObjectId: null,
				userPersisted: false
			};
			this.streamFailed = false;
			this.$nextTick(this.scrollToBottom);
			let conversationId = this.conversationId;
			try {
				if (!conversationId) {
					const created = await this.atrium.conversations.create({ model: this.chatModel });
					const row = mapConversation((created && created.conversation) || created) || {};
					conversationId = row.objectId;
					this.skipHydrate = true;
					this.replaceQuery(conversationId);
					this.conversations = [row].concat(this.conversations);
				}
				if (!this.streaming) return;
				const userSaved = await this.persistUserMessage(conversationId, userKey, text);
				if (!this.streaming) return;
				const messages = this.historyPayload();
				await this.streamAssistant(messages, conversationId);
				const assistantText = this.streamingText;
				const thinkingText = this.streamingThinking;
				if (!assistantText) {
					this.markStreamFailed();
					return;
				}
				this.finishPendingAssistant(assistantText, thinkingText);
				this.streamingText = '';
				this.streamingThinking = '';
				this.saving = true;
				const saved = userSaved
					? await this.atrium.conversations.messages.create(conversationId, {
						assistantContent: assistantText,
						model: this.chatModel
					})
					: await this.atrium.conversations.messages.create(conversationId, {
						userContent: text,
						assistantContent: assistantText,
						model: this.chatModel
					});
				this.applySavedIds(userKey, assistantKey, (saved && saved.messages) || []);
				await this.loadConversations();
			} catch (error) {
				if (error && error.name === 'AbortError') return;
				console.error(error);
				this.markStreamFailed();
			} finally {
				this.saving = false;
				this.streaming = false;
				this.streamController = null;
				this.pendingAssistantKey = null;
			}
		},
		/**
		 * Stream an assistant reply from Atrium into streamingText.
		 * @param {Array} messages
		 * @param {string} conversationId
		 * @returns {Promise<void>}
		 */
		async streamAssistant(messages, conversationId) {
			if (!this.atrium) throw new Error('Atrium is not connected');
			this.streaming = true;
			this.streamingText = '';
			this.streamingThinking = '';
			const controller = new AbortController();
			this.streamController = controller;
			try {
				const stream = this.atrium.chat.stream({
					model: this.chatModel,
					conversationId: conversationId,
					messages: messages
				}, { signal: controller.signal });
				for await (const frame of stream) {
					if (controller.signal.aborted) return;
					if (frame && frame.error) throw new Error(frame.error);
					if (frame && typeof frame.thinking === 'string') {
						this.streamingThinking += frame.thinking;
						this.patchPendingAssistant({ thinking: this.streamingThinking });
					}
					if (frame && typeof frame.delta === 'string') {
						this.streamingText += frame.delta;
						this.patchPendingAssistant({ content: this.streamingText });
					}
					if (frame && (frame.delta != null || frame.thinking != null)) {
						this.$nextTick(this.scrollToBottom);
					}
				}
			} catch (error) {
				if (error && (error.name === 'AbortError' || (controller && controller.signal.aborted))) return;
				throw error;
			} finally {
				this.streaming = false;
			}
		},
		/**
		 * Abort the in-flight stream and drop partial tokens.
		 */
		stop() {
			if (this.streamController) {
				this.streamController.abort();
				this.streamController = null;
			}
			this.streaming = false;
			this.streamingText = '';
			this.streamingThinking = '';
			this.streamFailed = false;
			this.dropPendingAssistant();
		},
		/**
		 * Copy an assistant reply to the clipboard and play the copy icon animation.
		 * @param {object} message
		 */
		async copyMessage(message) {
			const text = String(message && message.content || '');
			if (!text) return;
			try {
				await navigator.clipboard.writeText(text);
				this.markCopied(message);
			} catch (error) {
				window.M && window.M.toast && window.M.toast({ html: 'Failed to copy' });
			}
		},
		/**
		 * True when this message just copied.
		 * @param {object} message
		 * @returns {boolean}
		 */
		isCopied(message) {
			const key = message && (message.rowKey || message.objectId);
			return !!key && key === this.copiedKey;
		},
		/**
		 * Show the copy check animation, then restore the copy icon.
		 * @param {object} message
		 */
		markCopied(message) {
			const key = message && (message.rowKey || message.objectId);
			if (!key) return;
			this.clearCopiedTimer();
			this.copiedAt = Date.now();
			this.copiedKey = key;
			const self = this;
			this.copiedTimer = setTimeout(function() {
				self.copiedKey = null;
				self.copiedTimer = null;
			}, 1200);
		},
		/**
		 * Clear the copy-icon restore timer.
		 */
		clearCopiedTimer() {
			if (this.copiedTimer) {
				clearTimeout(this.copiedTimer);
				this.copiedTimer = null;
			}
		},
		/**
		 * Only the latest assistant turn can regenerate.
		 * @param {object} message
		 * @returns {boolean}
		 */
		canRegenerate(message) {
			const key = message && (message.rowKey || message.objectId);
			for (let i = this.messages.length - 1; i >= 0; i--) {
				if (this.messages[i].senderType === 'SYSTEM') {
					return (this.messages[i].rowKey || this.messages[i].objectId) === key;
				}
			}
			return false;
		},
		/**
		 * Re-stream the last assistant turn and PATCH it.
		 * @param {object} message
		 */
		async regenerate(message) {
			if (!this.atrium || !this.conversationId || this.busy) return;
			const key = message.rowKey || message.objectId;
			const idx = this.messages.findIndex(function(m) {
				return (m.rowKey || m.objectId) === key;
			});
			if (idx < 0) return;
			const prior = this.messages.slice(0, idx);
			this.messages = prior;
			this.beginAssistantTurn(key, message.objectId);
			this.failedRetry = {
				mode: 'regenerate',
				userText: null,
				userKey: null,
				assistantKey: key,
				assistantObjectId: message.objectId
			};
			this.streamFailed = false;
			this.$nextTick(this.scrollToBottom);
			try {
				const payload = prior.slice(-10).map(function(row) {
					return {
						role: row.senderType === 'USER' ? 'user' : 'assistant',
						content: row.content || ''
					};
				});
				await this.streamAssistant(payload, this.conversationId);
				const assistantText = this.streamingText;
				const thinkingText = this.streamingThinking;
				if (!assistantText) {
					this.markStreamFailed();
					return;
				}
				this.finishPendingAssistant(assistantText, thinkingText);
				this.streamingText = '';
				this.streamingThinking = '';
				this.saving = true;
				const saved = await this.atrium.requestJson(
					'PATCH',
					'/api/atrium/conversations/' + this.conversationId + '/messages/' + message.objectId,
					{ json: { content: assistantText, model: this.chatModel } }
				);
				const row = (saved && saved.message) || message;
				const last = this.messages[this.messages.length - 1];
				if (last && (last.rowKey || last.objectId) === key) {
					last.objectId = row.id || row.objectId || last.objectId;
					last.content = assistantText;
					last.thinking = thinkingText;
				}
			} catch (error) {
				if (error && error.name === 'AbortError') return;
				console.error(error);
				this.markStreamFailed();
			} finally {
				this.saving = false;
				this.streaming = false;
				this.streamController = null;
				this.pendingAssistantKey = null;
			}
		},
		/**
		 * Replace or prepend a conversation in the sidebar list.
		 * @param {object} row
		 */
		mergeConversation(row) {
			const mapped = mapConversation(row);
			if (!mapped || !mapped.objectId) return;
			const rest = this.conversations.filter(function(c) { return c.objectId !== mapped.objectId; });
			this.conversations = [mapped].concat(rest);
		},
		/**
		 * Keep local message rows; only copy Atrium ids after save.
		 * @param {string} userKey
		 * @param {string} assistantKey
		 * @param {Array} messages
		 */
		applySavedIds(userKey, assistantKey, messages) {
			const list = Array.isArray(messages) ? messages : [];
			let user = null;
			let assistant = null;
			for (let i = 0; i < list.length; i++) {
				if (list[i].senderType === 'USER') user = list[i];
				else assistant = list[i];
			}
			for (let i = 0; i < this.messages.length; i++) {
				const row = this.messages[i];
				if (user && (row.rowKey || row.objectId) === userKey) {
					row.objectId = user.id || user.objectId;
				}
				if (assistant && (row.rowKey || row.objectId) === assistantKey) {
					row.objectId = assistant.id || assistant.objectId;
				}
			}
		},
		/**
		 * Sanitize markdown to HTML for assistant bubbles.
		 * @param {string} content
		 * @returns {string}
		 */
		markdownHtml(content) {
			return renderMarkdown(content);
		},
		/**
		 * Keep the latest bubble in view.
		 */
		scrollToBottom() {
			const el = this.$refs.transcript;
			if (!el) return;
			el.scrollTop = el.scrollHeight;
		},
		/**
		 * Grow the composer textarea with the draft.
		 */
		resizeComposer() {
			const el = this.$refs.composer;
			if (!el) return;
			el.style.height = 'auto';
			const next = Math.min(Math.max(el.scrollHeight, 88), 160);
			el.style.height = next + 'px';
		}
	}
};
</script>
